<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../../components/Button.svelte';
  import BackButton from '../../../components/BackButton.svelte';
  import Table from '../../../components/Table.svelte';
  import Pagination from '../../../components/Pagination.svelte';
  import { CharityDonationRequestService, type CharityDonationRequest, type PaginationInfo } from '../../../lib/api/charityServices';
  import { formatDate } from '../../../lib/utils/formatters';
  import { setCurrentDonation } from '../../../lib/stores/donation-request.store';
  import { getCharityDonationListStatus } from '../../../lib/utils/charity-donation-status';

  export let userId: string = '';
  
  let loading = false;
  let error: string | null = null;
  let user: any = null;
  let donations: CharityDonationRequest[] = [];
  let currentUserId: string = '';
  let isLoading = false;
  
  // Pagination
  let currentPage = 1;
  let itemsPerPage = 20;
  let paging: PaginationInfo | null = null;

  async function loadUserDetails(id: string) {
    if (!id) {
      error = 'No user ID provided';
      loading = false;
      isLoading = false;
      return;
    }

    if (isLoading || (currentUserId === id && user && donations.length > 0)) {
      return;
    }

    isLoading = true;
    currentUserId = id;
    loading = true;
    error = null;
    
    try {
      // Load donations filtered by user_uuid
      const result = await CharityDonationRequestService.getDonationRequests({
        user_uuid: id,
        page: currentPage,
        per_page: itemsPerPage,
      });

      if (result.success && result.donations) {
        donations = result.donations;
        paging = result.paging || null;

        // Extract user info from first donation if available
        if (donations.length > 0 && donations[0].user) {
          user = {
            uuid: donations[0].user.uuid,
            full_name: donations[0].user.full_name || donations[0].user_name || 'Unknown User',
            email: donations[0].user.email || 'N/A',
          };
        } else if (donations.length > 0) {
          // Fallback if user data not in donation
          user = {
            uuid: id,
            full_name: donations[0].user_name || 'Unknown User',
            email: donations[0].email || 'N/A',
          };
        } else {
          // No donations found, but try to set basic user info
          user = {
            uuid: id,
            full_name: 'Unknown User',
            email: 'N/A',
          };
        }
      } else {
        error = result.error || 'Failed to load user donations';
        donations = [];
        paging = null;
      }
    } catch (err: any) {
      console.error('Error loading user details:', err);
      error = err.message || 'Failed to load user details';
      donations = [];
      paging = null;
    } finally {
      loading = false;
      isLoading = false;
    }
  }

  function formatCurrency(value: number | undefined): string {
    if (value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  function getDonationDisplayStatus(row: CharityDonationRequest): string {
    return getCharityDonationListStatus(row);
  }

  function getDonationStatusClass(displayStatus: string): string {
    switch (displayStatus) {
      case 'Pending': return 'incoming';
      case 'Confirmed Received': return 'received';
      case 'Pending Receival Confirmation': return 'pending-receival';
      default: return 'incoming';
    }
  }

  function getDonationStatusIcon(displayStatus: string): string {
    switch (displayStatus) {
      case 'Pending':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 15v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM4 7v2h12V7H4zm0 4v2h8v-2H4z"/></svg>';
      case 'Confirmed Received':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'Pending Receival Confirmation':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
      default:
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 15v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM4 7v2h12V7H4zm0 4v2h8v-2H4z"/></svg>';
    }
  }

  function handleDonationClick(donation: CharityDonationRequest) {
    if (donation.uuid) {
      setCurrentDonation(donation);
      push(`/donation-request/${donation.uuid}`);
    }
  }

  function handlePageChange(event: CustomEvent<{page: number}>) {
    currentPage = event.detail.page;
    loadUserDetails(userId);
  }

  // Table columns
  const columns = [
    {
      key: 'charity_name',
      label: 'Charity',
      width: '20%',
      render: (value: any, row: CharityDonationRequest) => {
        return row.charity?.name || 'N/A';
      }
    },
    {
      key: 'employer_name',
      label: 'Employer',
      width: '20%',
      render: (value: any, row: CharityDonationRequest) => {
        return row.employer?.name || row.employer_name || 'N/A';
      }
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '10%',
      render: (value: any, row: CharityDonationRequest) => {
        return `${row.leave_hours || row.hours || 0} hrs`;
      }
    },
    {
      key: 'leave_value',
      label: 'Value',
      width: '15%',
      render: (value: any, row: CharityDonationRequest) => {
        return formatCurrency(row.leave_value || row.value);
      }
    },
    {
      key: 'date',
      label: 'Date',
      width: '15%',
      render: (value: any, row: CharityDonationRequest) => {
        return formatDate(row.created_at || row.date);
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '18%',
      align: 'center',
      render: (value: any, row: CharityDonationRequest) => {
        const displayStatus = getDonationDisplayStatus(row);
        const statusClass = getDonationStatusClass(displayStatus);
        const icon = getDonationStatusIcon(displayStatus);
        return `<div class="donation-status-badge status-${statusClass}">${icon}<span>${displayStatus}</span></div>`;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '5%',
      render: (value: any, row: CharityDonationRequest) => {
        return `<button class="view-btn" data-uuid="${row.uuid}">View</button>`;
      }
    }
  ];

  afterUpdate(() => {
    // Handle view button clicks
    const viewButtons = document.querySelectorAll('.view-btn');
    viewButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const uuid = btn.getAttribute('data-uuid');
        if (uuid) {
          const donation = donations.find(d => d.uuid === uuid);
          if (donation) {
            handleDonationClick(donation);
          }
        }
      });
    });
  });

  // Handle row clicks
  function handleRowClick(row: any) {
    const donation = donations.find(d => d.uuid === row.uuid);
    if (donation) {
      handleDonationClick(donation);
    }
  }

  // Load user when userId changes
  $: {
    if (userId && userId !== currentUserId) {
      error = null;
      user = null;
      donations = [];
      currentPage = 1;
      loadUserDetails(userId);
    } else if (!userId && !loading && !user) {
      if (currentUserId !== '') {
        error = 'No user ID provided';
      }
    }
  }

  // Also try to load on mount as fallback
  onMount(() => {
    if (userId && !user && !loading) {
      loadUserDetails(userId);
    }
  });
</script>

<div class="user-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading user donations...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => loadUserDetails(userId)}>Try Again</Button>
    </div>
  {:else if user}
    <div class="detail-container">
      <!-- User Information Section -->
      <div class="info-section">
        <h2>User Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Name</span>
            <span class="value-large">{user.full_name}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Email</span>
            <span>
              <a href="mailto:{user.email}" class="email-link">
                {user.email}
              </a>
            </span>
          </div>
        </div>
      </div>

      <!-- Donations Section -->
      <div class="info-section">
        <h2>Donations ({donations.length})</h2>
        {#if donations.length > 0}
          <div class="table-section">
            <Table 
              columns={columns}
              data={donations}
              onRowClick={handleRowClick}
            />
          </div>
          
          {#if paging && paging.last_page > 1}
            <div class="pagination-container">
              <Pagination 
                currentPage={currentPage} 
                totalPages={paging.last_page} 
                on:pageChange={handlePageChange} 
              />
            </div>
          {/if}
        {:else}
          <div class="no-data-container">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="no-data-icon">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <h3>No Donations Found</h3>
            <p>This user hasn't made any donations yet.</p>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .user-detail-page {
    padding: 1.5rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: var(--color-negative, #dc2626);
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .email-link {
    color: var(--color-primary, #3b82f6);
    text-decoration: none;
    transition: color 0.2s;
  }

  .email-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-top: 1rem;
  }

  /* Donation status badges (Incoming, Confirmed Received, Pending Receival Confirmation) */
  :global(.donation-status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    gap: 0.375rem;
  }

  :global(.donation-status-badge svg) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  :global(.donation-status-badge.status-incoming) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.donation-status-badge.status-received) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.donation-status-badge.status-pending-receival) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .no-data-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .no-data-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .no-data-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  @media (max-width: 768px) {
    .user-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
